import { useCallback, useMemo, useState } from "react";
import {
  ReportListResponseDto,
  ReportStatus,
  useLazyDownloadReportByReportIdQuery,
} from "../../actions/report.action";
import Badge from "../../components/badge";
import Button from "../../components/button";
import Table, { TableColumns } from "../../components/table";
import { ReactComponent as DownloadIcon } from "../../images/download.svg";
import { dateToDDMMYYYY, splitKeyWithSpace } from "../../utils";

const COLUMNS_FOR_REPORTS_TABLE: TableColumns[] = [
  { label: "Report Type", field: "reportType" },
  { label: "Start Date", field: "fromDate" },
  { label: "End Date", field: "toDate" },
  { label: "Status", field: "status" },
  { label: "Created At", field: "createdAt" },
  { label: "Download", field: "downloadBtn", style: { textAlign: "center" } },
];

function getTableDataForReportsTable(context: {
  data: ReportListResponseDto | undefined;
  onDownloadReport: (reportId: string) => void;
  downloadingReportId: string | null;
}) {
  const { data, onDownloadReport, downloadingReportId } = context;
  if (!data || data.length === 0) return [];

  return data.map(
    ({ _id, fromDate, toDate, status, createdAt, reportType }) => ({
      reportType: splitKeyWithSpace(reportType),
      fromDate: dateToDDMMYYYY(new Date(fromDate)),
      toDate: dateToDDMMYYYY(new Date(toDate)),
      status: <StatusBadge status={status} />,
      createdAt: dateToDDMMYYYY(new Date(createdAt)),
      downloadBtn: (
        <Button
          loading={downloadingReportId === _id}
          disabled={status !== "SUCCEEDED"}
          className="d-flex align-items-center justify-content-center"
          variant="light-blue"
          size="sm"
          onClick={() => onDownloadReport(_id)}
        >
          <DownloadIcon />
        </Button>
      ),
    })
  );
}

interface ReportTableProps {
  reportList: ReportListResponseDto | undefined;
  isReportListLoading: boolean;
}

export default function ReportsTable({
  reportList,
  isReportListLoading,
}: ReportTableProps) {
  const [triggerDownloadReportByReportId] =
    useLazyDownloadReportByReportIdQuery();
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(
    null
  );

  const handleDownloadReport = useCallback(
    async (reportId: string) => {
      try {
        setDownloadingReportId(reportId);
        const signedUrl = await triggerDownloadReportByReportId({
          reportId,
        }).unwrap();

        if (!signedUrl) return;

        window.location.href = signedUrl;
      } catch (error) {
        console.log("Error downloading report:", error);
      } finally {
        setDownloadingReportId(null);
      }
    },
    [triggerDownloadReportByReportId]
  );

  const tableData = useMemo(() => {
    return getTableDataForReportsTable({
      data: reportList,
      onDownloadReport: handleDownloadReport,
      downloadingReportId,
    });
  }, [handleDownloadReport, downloadingReportId, reportList]);

  return (
    <div className="dashboard-container">
      <Table
        loading={isReportListLoading}
        data={tableData}
        columns={COLUMNS_FOR_REPORTS_TABLE}
      />
    </div>
  );
}

interface StatusBadgeProps {
  status: ReportStatus;
}
function StatusBadge({ status }: StatusBadgeProps) {
  if (status === "RUNNING") {
    const statusName = "INITIATED";
    return <Badge type="pending">{statusName}</Badge>;
  }
  if (status === "PENDING") return <Badge type="pending">{status}</Badge>;
  if (status === "FAILED") return <Badge type="reject">{status}</Badge>;
  return <Badge type="success">{status}</Badge>;
}
